import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { Save, AlertTriangle, Settings } from 'lucide-react';
import toast from 'react-hot-toast';

const AdminSettings = () => {
  const queryClient = useQueryClient();
  const [defaultPrompt, setDefaultPrompt] = useState('');
  const [defaultNoContextMessage, setDefaultNoContextMessage] = useState('');

  // Get default system prompt
  const { data: defaultPromptSetting, isLoading: promptLoading } = useQuery(
    'defaultSystemPrompt',
    async () => {
      try {
        const response = await axios.get('/settings/defaultSystemPrompt');
        return response.data;
      } catch (error) {
        if (error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    {
      onSuccess: (data) => {
        if (data?.value) {
          setDefaultPrompt(data.value);
        }
      }
    }
  );

  // Get default no context message
  const { data: defaultNoContextSetting, isLoading: noContextLoading } = useQuery(
    'defaultNoContextMessage',
    async () => {
      try {
        const response = await axios.get('/settings/defaultNoContextMessage');
        return response.data;
      } catch (error) {
        if (error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    {
      onSuccess: (data) => {
        if (data?.value) {
          setDefaultNoContextMessage(data.value);
        }
      }
    }
  );

  const isLoading = promptLoading || noContextLoading;

  // Initialize default settings
  const initializeMutation = useMutation(
    async () => {
      const response = await axios.post('/settings/initialize');
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('defaultSystemPrompt');
        queryClient.invalidateQueries('defaultNoContextMessage');
        toast.success('Default settings initialized');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to initialize settings');
      }
    }
  );

  // Update default prompt
  const updatePromptMutation = useMutation(
    async (prompt) => {
      const response = await axios.put('/settings/defaultSystemPrompt', {
        value: prompt,
        description: 'Default system prompt used for all clients unless they have a custom one'
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('defaultSystemPrompt');
        toast.success('Default system prompt updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update default prompt');
      }
    }
  );

  // Update default no context message
  const updateNoContextMutation = useMutation(
    async (message) => {
      const response = await axios.put('/settings/defaultNoContextMessage', {
        value: message,
        description: 'Default message shown when no relevant context is found'
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('defaultNoContextMessage');
        toast.success('Default no context message updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update default no context message');
      }
    }
  );

  const handleSavePrompt = () => {
    updatePromptMutation.mutate(defaultPrompt);
  };

  const handleSaveNoContext = () => {
    updateNoContextMutation.mutate(defaultNoContextMessage);
  };

  const handleInitialize = () => {
    initializeMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
        <div className="h-96 bg-gray-300 rounded"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Settings className="h-8 w-8 mr-3 text-gray-600" />
          Admin Settings
        </h1>
        <p className="text-gray-600 mt-2">
          Manage system-wide settings and default configurations
        </p>
      </div>

      {/* Default System Prompt Section */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Default System Prompt</h2>
            <p className="text-sm text-gray-600 mt-1">
              This prompt will be used for all clients unless they have a custom prompt configured
            </p>
          </div>
          {(!defaultPromptSetting || !defaultNoContextSetting) && (
            <button
              onClick={handleInitialize}
              disabled={initializeMutation.isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {initializeMutation.isLoading ? 'Initializing...' : 'Initialize Defaults'}
            </button>
          )}
        </div>

        {defaultPromptSetting ? (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Template Variable:</p>
                  <p>Use <code className="bg-blue-100 px-1 rounded">{'{contextText}'}</code> where you want client context information to be inserted.</p>
                  <p className="mt-2">
                    <strong>Note:</strong> Changes to this default prompt will affect all clients who use the default setting.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                System Prompt Template
              </label>
              <textarea
                value={defaultPrompt}
                onChange={(e) => setDefaultPrompt(e.target.value)}
                placeholder="Enter the default system prompt..."
                className="w-full h-80 p-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical font-mono text-sm"
              />
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-gray-500">
                Last updated: {new Date(defaultPromptSetting.updatedAt).toLocaleString()}
              </div>
              <button
                onClick={handleSavePrompt}
                disabled={updatePromptMutation.isLoading || !defaultPrompt.trim()}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4 mr-2" />
                {updatePromptMutation.isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Settings className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Default Settings Found
            </h3>
            <p className="text-gray-600 mb-6">
              Initialize the default settings to get started
            </p>
          </div>
        )}
      </div>

      {/* Default No Context Message Section */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Default "No Context" Message</h2>
          <p className="text-sm text-gray-600 mt-1">
            This message will be shown when no relevant context is found for a user's question
          </p>
        </div>

        {defaultNoContextSetting ? (
          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm text-orange-800">
                  <p className="font-medium">Important:</p>
                  <p>
                    This message is displayed when users ask questions that don't match any of the client's context information.
                    Make it helpful and encourage users to contact the business directly.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                No Context Message
              </label>
              <textarea
                value={defaultNoContextMessage}
                onChange={(e) => setDefaultNoContextMessage(e.target.value)}
                placeholder="Enter the default message to show when no context is found..."
                className="w-full h-32 p-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
              />
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-gray-500">
                Last updated: {new Date(defaultNoContextSetting.updatedAt).toLocaleString()}
              </div>
              <button
                onClick={handleSaveNoContext}
                disabled={updateNoContextMutation.isLoading || !defaultNoContextMessage.trim()}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4 mr-2" />
                {updateNoContextMutation.isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <AlertTriangle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Default "No Context" Message Found
            </h3>
            <p className="text-gray-600 mb-6">
              Initialize the default settings to configure this message
            </p>
          </div>
        )}
      </div>

      {/* Additional Settings Section - Future Use */}
      <div className="bg-white shadow rounded-lg p-6 mt-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Additional Settings</h2>
        <p className="text-gray-500 italic">More configuration options will be available here in future updates.</p>
      </div>
    </div>
  );
};

export default AdminSettings;